'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Link as LinkIcon, 
  Table,
  CheckCircle,
  WarningCircle,
  Globe,
} from '@phosphor-icons/react';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { Button } from '../../ui/Button';

// 平台类型
export type OnlineSheetProvider = 'google_sheets' | 'feishu' | 'tencent_docs';

// 配置接口
export interface OnlineSheetConfigData {
  provider?: OnlineSheetProvider;
  sheetUrl?: string;
  sheetName?: string;
  rangeMode?: 'auto' | 'range';
  startRow?: number;
  endRow?: number;
  headerRow?: number;  // 表头行号，0 表示无表头（自动生成列名）
  accessToken?: string;
  refreshToken?: string;
  tokenExpireAt?: number;
  isAuthorized?: boolean;
}

interface OnlineSheetConfigProps {
  nodeId: string;
  config: OnlineSheetConfigData;
}

// 平台配置
const PROVIDERS = [
  {
    value: 'google_sheets' as OnlineSheetProvider,
    label: 'nodes.config.online_sheet.providers.googleSheets',
    icon: 'https://www.google.com/favicon.ico',
    color: 'from-blue-500 to-green-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
  },
  {
    value: 'feishu' as OnlineSheetProvider,
    label: 'nodes.config.online_sheet.providers.feishu',
    icon: null, // 使用文字图标
    color: 'from-blue-400 to-cyan-400',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
  },
  {
    value: 'tencent_docs' as OnlineSheetProvider,
    label: 'nodes.config.online_sheet.providers.tencentDocs',
    icon: null,
    color: 'from-sky-500 to-blue-600',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-200',
    textColor: 'text-sky-600',
  },
];

export function OnlineSheetConfig({ nodeId, config }: OnlineSheetConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();

  // 本地状态
  const [provider, setProvider] = useState<OnlineSheetProvider>(config.provider || 'google_sheets');
  const [sheetUrl, setSheetUrl] = useState(config.sheetUrl || '');
  const [sheetName, setSheetName] = useState(config.sheetName || '');
  const [rangeMode, setRangeMode] = useState<'auto' | 'range'>(config.rangeMode || 'auto');
  const [startRow, setStartRow] = useState(config.startRow || 1);
  const [endRow, setEndRow] = useState(config.endRow || 100);
  const [headerRow, setHeaderRow] = useState(config.headerRow || 1);  // 默认第1行为表头
  const [isAuthorized, setIsAuthorized] = useState(config.isAuthorized || false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [urlError, setUrlError] = useState('');

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.provider) setProvider(config.provider);
    if (config.sheetUrl) setSheetUrl(config.sheetUrl);
    if (config.sheetName) setSheetName(config.sheetName);
    if (config.rangeMode) setRangeMode(config.rangeMode);
    if (config.startRow) setStartRow(config.startRow);
    if (config.endRow) setEndRow(config.endRow);
    if (typeof config.headerRow === 'number') setHeaderRow(config.headerRow);
    if (typeof config.isAuthorized === 'boolean') setIsAuthorized(config.isAuthorized);
  }, [config]);

  // 保存配置
  const saveConfig = (updates: Partial<OnlineSheetConfigData>) => {
    const newConfig = {
      provider,
      sheetUrl,
      sheetName,
      rangeMode,
      startRow,
      endRow,
      headerRow,
      isAuthorized,
      ...updates,
    };
    updateNodeConfig(nodeId, { type: 'online_sheet', params: newConfig });
  };

  // 处理平台切换
  const handleProviderChange = (newProvider: OnlineSheetProvider) => {
    setProvider(newProvider);
    setIsAuthorized(false);
    saveConfig({ provider: newProvider, isAuthorized: false });
  };

  // 验证 URL
  const validateUrl = (url: string, prov: OnlineSheetProvider): boolean => {
    if (!url) return false;
    
    switch (prov) {
      case 'google_sheets':
        return /\/d\/[a-zA-Z0-9-_]+/.test(url);
      case 'feishu':
        return /\/sheets\/[a-zA-Z0-9]+/.test(url);
      case 'tencent_docs':
        return /\/d\/[a-zA-Z0-9]+/.test(url) || /\/sheet\/[a-zA-Z0-9]+/.test(url);
      default:
        return false;
    }
  };

  // 处理 URL 变化
  const handleUrlChange = (value: string) => {
    setSheetUrl(value);
    const isValid = validateUrl(value, provider);
    setUrlError(isValid || !value ? '' : t('nodes.config.online_sheet.urlError'));
    saveConfig({ sheetUrl: value });
  };

  // 处理授权
  const handleAuthorize = async () => {
    setIsAuthorizing(true);
    // 模拟授权流程
    setTimeout(() => {
      setIsAuthorized(true);
      setIsAuthorizing(false);
      saveConfig({ isAuthorized: true });
    }, 1500);
  };

  // 处理表头行号变化
  const handleHeaderRowChange = (value: number) => {
    const newValue = Math.max(0, value);  // 最小为0
    setHeaderRow(newValue);
    saveConfig({ headerRow: newValue });
  };

  // 获取 URL 占位符
  const getUrlPlaceholder = () => {
    switch (provider) {
      case 'google_sheets':
        return 'https://docs.google.com/spreadsheets/d/.../edit';
      case 'feishu':
        return 'https://xxx.feishu.cn/sheets/...';
      case 'tencent_docs':
        return 'https://docs.qq.com/sheet/...';
      default:
        return '';
    }
  };

  return (
    <div className="space-y-5">
      {/* 平台选择 */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
          {t('nodes.config.online_sheet.platform')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {PROVIDERS.map((prov) => (
            <button
              key={prov.value}
              onClick={() => handleProviderChange(prov.value)}
              className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all duration-200 ${
                provider === prov.value
                  ? `${prov.bgColor} ${prov.borderColor} border-current`
                  : 'bg-white border-zinc-200 hover:border-zinc-300'
              }`}
            >
              {provider === prov.value && (
                <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full ${prov.textColor.replace('text-', 'bg-')} text-white flex items-center justify-center`}>
                  <CheckCircle className="w-3.5 h-3.5" weight="fill" />
                </div>
              )}
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${prov.color} flex items-center justify-center mb-1.5`}>
                {prov.icon ? (
                  <img src={prov.icon} alt="" className="w-4 h-4" />
                ) : (
                  <Table className="w-4 h-4 text-white" weight="fill" />
                )}
              </div>
              <span className={`text-[10px] font-medium text-center ${
                provider === prov.value ? prov.textColor : 'text-zinc-600'
              }`}>
                {t(prov.label.replace('onlineSheet', 'online_sheet'))}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 授权状态 */}
      <div className={`p-3 rounded-xl border ${isAuthorized ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isAuthorized ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
              {isAuthorized ? <CheckCircle className="w-4 h-4" weight="fill" /> : <WarningCircle className="w-4 h-4" weight="fill" />}
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-700">
                {isAuthorized ? t('nodes.config.online_sheet.authorized') : t('nodes.config.online_sheet.notAuthorized')}
              </div>
              <div className="text-[10px] text-zinc-500">
                {isAuthorized 
                  ? t('nodes.config.online_sheet.authorizedDesc') 
                  : t('nodes.config.online_sheet.authorizeDesc')}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            variant={isAuthorized ? "secondary" : "primary"}
            onClick={handleAuthorize}
            disabled={isAuthorizing || isAuthorized}
            className={isAuthorized ? 'text-emerald-600 border-emerald-300' : ''}
          >
            {isAuthorizing 
              ? t('nodes.config.online_sheet.authorizing') 
              : isAuthorized 
                ? t('nodes.config.online_sheet.authorized') 
                : t('nodes.config.online_sheet.authorize')}
          </Button>
        </div>
      </div>

      {/* 表格地址 */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
          {t('nodes.config.online_sheet.sheetUrl')}
        </label>
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={sheetUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder={getUrlPlaceholder()}
            className={`w-full pl-9 pr-3 py-2.5 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors ${
              urlError ? 'border-rose-300 focus:border-rose-500' : 'border-zinc-200'
            }`}
          />
        </div>
        <AnimatePresence>
          {urlError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-[10px] text-rose-500 flex items-center gap-1"
            >
              <WarningCircle className="w-3 h-3" />
              {urlError}
            </motion.div>
          )}
        </AnimatePresence>
        <p className="text-[10px] text-zinc-400">
          {t('nodes.config.online_sheet.urlHelp')}
        </p>
      </div>

      {/* Sheet 名称 */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
          {t('nodes.config.online_sheet.sheetName')}
        </label>
        <div className="relative">
          <Table className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={sheetName}
            onChange={(e) => { setSheetName(e.target.value); saveConfig({ sheetName: e.target.value }); }}
            placeholder={t('nodes.config.online_sheet.sheetNamePlaceholder')}
            className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
          />
        </div>
        <p className="text-[10px] text-zinc-400">
          {t('nodes.config.online_sheet.sheetNameHelp')}
        </p>
      </div>

      {/* 读取范围 */}
      <div className="space-y-3">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
          {t('nodes.config.online_sheet.rangeMode')}
        </label>
        <div className="flex gap-2 p-1 bg-zinc-100 rounded-lg">
          <button
            onClick={() => { setRangeMode('auto'); saveConfig({ rangeMode: 'auto' }); }}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              rangeMode === 'auto'
                ? 'bg-white text-zinc-700 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t('nodes.config.online_sheet.rangeAuto')}
          </button>
          <button
            onClick={() => { setRangeMode('range'); saveConfig({ rangeMode: 'range' }); }}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
              rangeMode === 'range'
                ? 'bg-white text-zinc-700 shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            {t('nodes.config.online_sheet.rangeSpecify')}
          </button>
        </div>

        {/* 指定范围时的行数设置 */}
        <AnimatePresence>
          {rangeMode === 'range' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-2 gap-3 overflow-hidden"
            >
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-zinc-500">
                  {t('nodes.config.online_sheet.startRow')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={startRow}
                  onChange={(e) => { 
                    const val = parseInt(e.target.value) || 1; 
                    setStartRow(val); 
                    saveConfig({ startRow: val }); 
                  }}
                  className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-zinc-500">
                  {t('nodes.config.online_sheet.endRow')}
                </label>
                <input
                  type="number"
                  min={startRow}
                  value={endRow}
                  onChange={(e) => { 
                    const val = parseInt(e.target.value) || startRow; 
                    setEndRow(val); 
                    saveConfig({ endRow: val }); 
                  }}
                  className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 表头行号设置 */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
          {t('nodes.config.online_sheet.headerRow')}
        </label>
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <input
              type="number"
              min={0}
              value={headerRow}
              onChange={(e) => handleHeaderRowChange(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400">
              {t('nodes.config.online_sheet.row')}
            </span>
          </div>
          <button
            onClick={() => handleHeaderRowChange(0)}
            className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
              headerRow === 0
                ? 'bg-zinc-100 text-zinc-500 border-zinc-200'
                : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            {t('nodes.config.online_sheet.noHeader')}
          </button>
        </div>
        <p className="text-[10px] text-zinc-500">
          {headerRow === 0 
            ? t('nodes.config.online_sheet.noHeaderHelp')
            : t('nodes.config.online_sheet.headerRowHelp', { row: headerRow })}
        </p>
      </div>

      {/* 提示信息 */}
      <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-start gap-2">
          <Globe className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-[11px] text-blue-700 leading-relaxed">
            {t('nodes.config.online_sheet.tip')}
          </div>
        </div>
      </div>
    </div>
  );
}
